import { logger } from '@automata/utils';

import {
  SOURCE_HANDLE_IDENTIFIER,
  TARGET_HANDLE_IDENTIFIER,
} from '../constants';
import {
  NEW_NODE_OVERLAP_OFFSET,
  NEW_NODE_OVERLAP_TOLERANCE,
} from '../features/graph/constants';
import type { NodePositionById } from '../features/graph/graphSlice';
import type { GraphEdges, NodePosition } from '../types';

export type LabwareDependencyHandleType = 'target' | 'source';

export function serializeLabwareHandlePairId(
  handleType: LabwareDependencyHandleType,
  handlePairId: string
): string {
  return `${handleType}.${handlePairId}`;
}

/** handles like "target.foo123" -> "foo123" for labware dependency edges */
export function parseHandlePairId(handle: string): string | null {
  const splitHandle = handle.split('.');
  const handlePairId = splitHandle[1];
  if ([2, 3].includes(splitHandle.length) && handlePairId != undefined) {
    return handlePairId;
  } else {
    logger.error(
      `unexpected handle: "${handle}". Could not parse handlePairId.`
    );
  }
  return null;
}

export function parseHandlePairType(
  handle: string
): LabwareDependencyHandleType | null {
  const splitHandle = handle.split('.');
  const handlePairType = splitHandle[0];
  if (
    [2, 3].includes(splitHandle.length) &&
    (handlePairType === TARGET_HANDLE_IDENTIFIER ||
      handlePairType === SOURCE_HANDLE_IDENTIFIER)
  ) {
    return handlePairType;
  }
  return null;
}

export function getHandlePairIdConnections(args: {
  labwareDependencyEdges: GraphEdges;
  nodeId: string;
  handlePairId: string;
}): { upstream: boolean; downstream: boolean } {
  const { labwareDependencyEdges, nodeId, handlePairId } = args;
  // PERF(IL, 2024-04-04): could do this in one iteration
  const upstream = labwareDependencyEdges.some(
    (e) =>
      e.target === nodeId &&
      e.targetHandle ===
        serializeLabwareHandlePairId(TARGET_HANDLE_IDENTIFIER, handlePairId)
  );
  const downstream = labwareDependencyEdges.some(
    (e) =>
      e.source === nodeId &&
      e.sourceHandle ===
        serializeLabwareHandlePairId(SOURCE_HANDLE_IDENTIFIER, handlePairId)
  );
  return { upstream, downstream };
}

export function combinePositions(
  a: NodePosition,
  b: NodePosition,
  op: (ca: number, cb: number) => number
): NodePosition {
  return {
    x: op(a.x, b.x),
    y: op(a.y, b.y),
  };
}

export function addPositions(a: NodePosition, b: NodePosition): NodePosition {
  return combinePositions(a, b, (ca, cb) => ca + cb);
}

export function subtractPositions(
  a: NodePosition,
  b: NodePosition
): NodePosition {
  return combinePositions(a, b, (ca, cb) => ca - cb);
}

export function getPositionsCenter(args: {
  positions: NodePositionById;
  selectedNodeIds: string[];
}): NodePosition {
  const { positions, selectedNodeIds } = args;

  const summedPositions = selectedNodeIds.reduce<NodePosition>(
    (acc, nodeId) => {
      const pos = positions[nodeId] ?? null;

      if (pos === null) {
        return acc;
      } else {
        return addPositions(acc, pos);
      }
    },
    { x: 0, y: 0 }
  );

  const meanPosition = {
    x: summedPositions.x / selectedNodeIds.length,
    y: summedPositions.y / selectedNodeIds.length,
  };

  return meanPosition;
}

const _isWithinTolerance = (a: number, b: number): boolean => {
  return Math.abs(b - a) <= NEW_NODE_OVERLAP_TOLERANCE;
};

export function determineNewNodePosition(
  newPosition: NodePosition,
  existingPositions: NodePosition[]
): NodePosition {
  let x = newPosition.x;
  let y = newPosition.y;
  while (
    existingPositions.some(
      (existingPos) =>
        _isWithinTolerance(existingPos.x, x) &&
        _isWithinTolerance(existingPos.y, y)
    )
  ) {
    x += NEW_NODE_OVERLAP_OFFSET;
    y += NEW_NODE_OVERLAP_OFFSET;
  }

  return { x, y };
}
